import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  UsersIcon,
  CalendarIcon,
  DocumentChartBarIcon,
  ArrowDownTrayIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  CurrencyDollarIcon,
  LanguageIcon,
  UserGroupIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { getCustomerPortalErrorMessage } from '../utils/apiErrorUtils';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const SupervisorDashboard = () => {
  const { makeAuthenticatedRequest, customer } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [trends, setTrends] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [claimants, setClaimants] = useState([]);
  const [selectedView, setSelectedView] = useState('overview');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [dateRange, setDateRange] = useState('all'); // all, month, week

  useEffect(() => {
    loadSupervisorData();
  }, [dateRange]);

  const loadSupervisorData = async () => {
    try {
      setLoading(true);

      // Calculate date range
      let startDate, endDate;
      if (dateRange === 'month') {
        endDate = new Date();
        startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 1);
      } else if (dateRange === 'week') {
        endDate = new Date();
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 7);
      }

      const dateParams = startDate && endDate 
        ? `?startDate=${startDate.toISOString().split('T')[0]}&endDate=${endDate.toISOString().split('T')[0]}`
        : '';

      // Load all data in parallel
      const [overviewRes, employeesRes, trendsRes, languagesRes, claimantsRes] = await Promise.all([
        makeAuthenticatedRequest(`${API_BASE}/customer/supervisor/overview${dateParams}`),
        makeAuthenticatedRequest(`${API_BASE}/customer/supervisor/employees${dateParams}`),
        makeAuthenticatedRequest(`${API_BASE}/customer/supervisor/trends?period=month&limit=6`),
        makeAuthenticatedRequest(`${API_BASE}/customer/supervisor/languages${dateParams}`),
        makeAuthenticatedRequest(`${API_BASE}/customer/supervisor/claimants?limit=10`)
      ]);

      // Set data from responses
      if (overviewRes.data) {
        setOverview(overviewRes.data);
      }

      if (employeesRes.data?.employees) {
        setEmployees(employeesRes.data.employees);
      }

      if (trendsRes.data?.trends) {
        setTrends(trendsRes.data.trends);
      }

      if (languagesRes.data?.languages) {
        setLanguages(languagesRes.data.languages);
      }

      if (claimantsRes.data?.claimants) {
        setClaimants(claimantsRes.data.claimants);
      }

    } catch (error) {
      console.error('Error loading supervisor data:', error);
      
      if (error.status === 403) {
        toast.error('You do not have supervisor permissions. Please contact your administrator.');
      } else {
        toast.error(getCustomerPortalErrorMessage(error, 'Error loading supervisor dashboard'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type) => {
    try {
      const dateParams = dateRange !== 'all' 
        ? `&startDate=${new Date(Date.now() - (dateRange === 'month' ? 30 : 7) * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}&endDate=${new Date().toISOString().split('T')[0]}`
        : '';

      const response = await makeAuthenticatedRequest(
        `${API_BASE}/customer/supervisor/export?type=${type}${dateParams}`,
        { responseType: 'blob' }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Export completed successfully');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Error exporting data'));
    }
  };

  const viewEmployeeDetails = async (employeeId) => {
    try {
      const response = await makeAuthenticatedRequest(
        `${API_BASE}/customer/supervisor/employees/${employeeId}`
      );

      if (response.data) {
        setSelectedEmployee(response.data);
        setSelectedView('employee-detail');
      }
    } catch (error) {
      console.error('Error loading employee details:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Error loading employee details'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check if user has permissions
  if (!customer?.role || (customer.role !== 'supervisor' && customer.role !== 'admin' && !customer.can_view_billing_account_data)) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <h2 className="text-2xl font-bold text-yellow-800 mb-2">Access Restricted</h2>
          <p className="text-yellow-700">
            You need supervisor permissions to access this dashboard. Please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-lg p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">Supervisor Dashboard</h1>
              <p className="text-indigo-100 text-lg">
                {overview?.billingAccount?.name || 'Billing Account'} - Team Analytics & Insights
              </p>
            </div>
            <BuildingOfficeIcon className="h-16 w-16 text-indigo-200" />
          </div>
        </div>
      </motion.div>

      {/* Date Range Filter */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setDateRange('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              dateRange === 'all'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All Time
          </button>
          <button
            onClick={() => setDateRange('month')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              dateRange === 'month'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Last Month
          </button>
          <button
            onClick={() => setDateRange('week')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              dateRange === 'week'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Last Week
          </button>
        </div>

        {customer?.can_export_reports && (
          <div className="flex gap-2">
            <button
              onClick={() => handleExport('employees')}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              Export Employees
            </button>
            <button
              onClick={() => handleExport('claimants')}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              Export Claimants
            </button>
          </div>
        )}
      </div>

      {/* View Tabs */}
      <div className="mb-6 flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setSelectedView('overview')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedView === 'overview'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setSelectedView('employees')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedView === 'employees'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Employees
        </button>
        <button
          onClick={() => setSelectedView('claimants')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedView === 'claimants'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Claimants
        </button>
      </div>

      {/* Overview Tab */}
      {selectedView === 'overview' && overview && (
        <div className="space-y-6">
          {/* Key Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              icon={CalendarIcon}
              title="Total Appointments"
              value={overview.stats.total_appointments}
              color="blue"
            />
            <StatCard
              icon={UsersIcon}
              title="Active Employees"
              value={overview.stats.active_employees}
              color="green"
            />
            <StatCard
              icon={UserGroupIcon}
              title="Total Claimants"
              value={overview.stats.total_claimants}
              color="purple"
            />
            <StatCard
              icon={LanguageIcon}
              title="Languages Used"
              value={overview.stats.languages_used}
              color="indigo"
            />
          </div>

          {/* Secondary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard
              icon={ClockIcon}
              title="Avg Duration"
              value={`${overview.stats.avg_duration_hours.toFixed(1)}h`}
              color="orange"
            />
            <StatCard
              icon={ArrowTrendingUpIcon}
              title="Completed"
              value={overview.stats.completed_appointments}
              subtitle={`of ${overview.stats.total_appointments} total`}
              color="green"
            />
            <StatCard
              icon={CalendarIcon}
              title="Upcoming"
              value={overview.stats.upcoming_appointments}
              color="blue"
            />
          </div>

          {/* Additional Insights */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Most Used Language */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <LanguageIcon className="h-6 w-6 text-indigo-600" />
                Most Used Language
              </h3>
              <p className="text-3xl font-bold text-indigo-600">{overview.mostUsedLanguage}</p>
            </div>

            {/* Most Used Service Type */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <DocumentChartBarIcon className="h-6 w-6 text-purple-600" />
                Most Used Service Type
              </h3>
              <p className="text-3xl font-bold text-purple-600">{overview.mostUsedServiceType}</p>
            </div>
          </div>

          {/* Language Distribution */}
          {languages.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <h3 className="text-lg font-semibold mb-4">Language Distribution</h3>
              <div className="space-y-3">
                {languages.map((lang, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="font-medium">{lang.language}</span>
                    <span className="text-gray-600">{lang.appointment_count} appointments</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trends Chart */}
          {trends.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <ChartBarIcon className="h-6 w-6 text-indigo-600" />
                Appointment Trends (Last 6 Months)
              </h3>
              <div className="space-y-3">
                {trends.map((trend, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <span className="text-sm font-medium w-32">
                      {(() => {
                        const dateStr = trend.period;
                        if (dateStr.includes('-') && !dateStr.includes('T')) {
                          const [year, month, day] = dateStr.split('-').map(Number);
                          return new Date(year, month - 1, day).toLocaleDateString('en-US', { 
                            month: 'short', 
                            year: 'numeric' 
                          });
                        }
                        return new Date(dateStr).toLocaleDateString('en-US', { 
                          month: 'short', 
                          year: 'numeric' 
                        });
                      })()}
                    </span>
                    <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                      <div
                        className="bg-indigo-600 h-6 rounded-full flex items-center justify-end pr-2"
                        style={{ width: `${Math.min((trend.total_appointments / Math.max(...trends.map(t => t.total_appointments))) * 100, 100)}%` }}
                      >
                        <span className="text-white text-xs font-medium">
                          {trend.total_appointments}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Employees Tab */}
      {selectedView === 'employees' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Employee Activity</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pending
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Claimants
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {employees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                          <div className="text-sm text-gray-500">{employee.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {employee.title || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {employee.total_appointments}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                        {employee.completed_appointments}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600 font-medium">
                        {employee.pending_appointments}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {employee.claimants_managed}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => viewEmployeeDetails(employee.id)}
                          className="text-indigo-600 hover:text-indigo-900 font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Claimants Tab */}
      {selectedView === 'claimants' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Claimant Overview</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Language
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Appointments
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employees Managing
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Appointment
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {claimants.map((claimant) => (
                    <tr key={claimant.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {claimant.first_name} {claimant.last_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {claimant.language || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {claimant.total_appointments}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {claimant.employees_managing}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {claimant.last_appointment_date 
                          ? (() => {
                              const dateStr = claimant.last_appointment_date;
                              if (dateStr.includes('-') && !dateStr.includes('T')) {
                                const [year, month, day] = dateStr.split('-').map(Number);
                                return new Date(year, month - 1, day).toLocaleDateString();
                              }
                              return new Date(dateStr).toLocaleDateString();
                            })()
                          : 'N/A'
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Employee Detail View */}
      {selectedView === 'employee-detail' && selectedEmployee && (
        <div className="space-y-6">
          <button
            onClick={() => {
              setSelectedView('employees');
              setSelectedEmployee(null);
            }}
            className="text-indigo-600 hover:text-indigo-900 font-medium flex items-center gap-2"
          >
            ← Back to Employees
          </button>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <h2 className="text-2xl font-bold mb-4">{selectedEmployee.employee.name}</h2>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">{selectedEmployee.employee.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Title</p>
                <p className="font-medium">{selectedEmployee.employee.title || 'N/A'}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <StatCard
                icon={CalendarIcon}
                title="Total"
                value={selectedEmployee.stats.total_appointments}
                color="blue"
                size="small"
              />
              <StatCard
                icon={ClockIcon}
                title="Completed"
                value={selectedEmployee.stats.completed_appointments}
                color="green"
                size="small"
              />
              <StatCard
                icon={UsersIcon}
                title="Claimants"
                value={selectedEmployee.stats.claimants_managed}
                color="purple"
                size="small"
              />
            </div>

            <h3 className="text-lg font-semibold mb-4">Recent Appointments</h3>
            <div className="space-y-3">
              {selectedEmployee.recentAppointments.map((appointment) => (
                <div key={appointment.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{appointment.title || appointment.job_number}</p>
                      <p className="text-sm text-gray-600">
                        {appointment.claimant_first_name} {appointment.claimant_last_name}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {(() => {
                          const dateStr = appointment.scheduled_date;
                          if (dateStr.includes('-') && !dateStr.includes('T')) {
                            const [year, month, day] = dateStr.split('-').map(Number);
                            return new Date(year, month - 1, day).toLocaleDateString();
                          }
                          return new Date(dateStr).toLocaleDateString();
                        })()}
                      </p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        appointment.status === 'completed' ? 'bg-green-100 text-green-800' :
                        appointment.status === 'pending_authorization' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {appointment.status}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 flex gap-4 text-sm text-gray-600">
                    <span>📋 {appointment.service_type}</span>
                    <span>🌐 {appointment.language}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, title, value, subtitle, color, size = 'normal' }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    orange: 'bg-orange-100 text-orange-600',
    yellow: 'bg-yellow-100 text-yellow-600'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-sm font-medium text-gray-600 mb-1 ${size === 'small' ? 'text-xs' : ''}`}>
            {title}
          </p>
          <p className={`font-bold text-gray-900 ${size === 'small' ? 'text-xl' : 'text-3xl'}`}>
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className={size === 'small' ? 'h-5 w-5' : 'h-8 w-8'} />
        </div>
      </div>
    </motion.div>
  );
};

export default SupervisorDashboard;

